import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Loader2, CheckCircle2, AlertCircle, Filter } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  useReconciliationStatus,
  useUpdateMergeDecision,
  useFinalizeReconciliation,
  useReconciliationRun,
} from '@/hooks/useReconciliation'
import { MergeGroupCard } from './MergeGroupCard'

interface ReconciliationViewProps {
  kbId: string
  reconciliationId: string
}

type FilterMode = 'all' | 'pending' | 'approved' | 'rejected'

function ProgressMessage(progress: number): string {
  if (progress < 20) return 'Starting analysis...'
  if (progress < 40) return 'Preparing intent data...'
  if (progress < 60) return 'Calling OpenAI API for semantic analysis...'
  if (progress < 80) return 'Processing LLM results...'
  if (progress < 100) return 'Creating merge suggestions...'
  return 'Complete!'
}

export function ReconciliationView({ kbId, reconciliationId }: ReconciliationViewProps) {
  const { toast } = useToast()
  const [filter, setFilter] = useState<FilterMode>('all')

  const { data: recon, isLoading } = useReconciliationStatus(kbId, reconciliationId)
  const updateDecision = useUpdateMergeDecision(kbId)
  const finalize = useFinalizeReconciliation(kbId)
  const rerunReconciliation = useReconciliationRun(kbId)

  if (isLoading || !recon) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading reconciliation...</span>
      </div>
    )
  }

  // ── Processing state ──
  if (recon.status === 'processing') {
    return (
      <div className="py-8 space-y-4">
        <div className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <h3 className="font-semibold">Analyzing Intents for Duplicates...</h3>
        </div>
        <Progress value={recon.progress} className="h-2" />
        <p className="text-sm text-muted-foreground">
          {recon.progress}% &mdash; {ProgressMessage(recon.progress)}
        </p>
      </div>
    )
  }

  // ── Failed state ──
  if (recon.status === 'failed') {
    return (
      <Alert variant="destructive" className="my-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Reconciliation Failed</AlertTitle>
        <AlertDescription className="mt-2">
          {recon.error_message || 'An unknown error occurred.'}
          <div className="mt-3">
            <Button
              size="sm"
              onClick={() => rerunReconciliation.mutate()}
              disabled={rerunReconciliation.isPending}
            >
              {rerunReconciliation.isPending ? 'Starting...' : 'Retry'}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    )
  }

  // ── Completed / pending_review state ──
  const summary = recon.summary
  const groups = recon.merge_groups ?? []
  const allDecided = summary.pending_review === 0
  const canFinalize = allDecided && !recon.finalized

  const filteredGroups = groups.filter((g) => {
    if (filter === 'pending') return g.user_approved === null
    if (filter === 'approved') return g.user_approved === true
    if (filter === 'rejected') return g.user_approved === false
    return true
  })

  const handleApprove = (groupId: string) => {
    updateDecision.mutate(
      { groupId, userApproved: true },
      {
        onError: (err: any) => {
          toast({ title: 'Failed to approve', description: err?.response?.data?.error || 'Something went wrong.', variant: 'destructive' })
        },
      }
    )
  }

  const handleReject = (groupId: string) => {
    updateDecision.mutate(
      { groupId, userApproved: false },
      {
        onError: (err: any) => {
          toast({ title: 'Failed to reject', description: err?.response?.data?.error || 'Something went wrong.', variant: 'destructive' })
        },
      }
    )
  }

  const handleFinalize = () => {
    finalize.mutate(reconciliationId, {
      onSuccess: () => {
        toast({ title: 'Reconciliation finalized', description: 'All decisions have been recorded.' })
      },
      onError: (err: any) => {
        toast({ title: 'Failed to finalize', description: err?.response?.data?.error || 'Something went wrong.', variant: 'destructive' })
      },
    })
  }

  // ── No duplicates found ──
  if (summary.total_groups === 0) {
    return (
      <div className="py-12 text-center">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="font-semibold text-lg">No Duplicate Intents Found</h3>
        <p className="text-sm text-muted-foreground mt-1">
          All intents appear to be semantically distinct. Your taxonomy is already optimized!
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Intent Reconciliation</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {recon.finalized ? 'Finalized' : 'Review AI-generated merge suggestions'}
          </p>
        </div>
        {recon.finalized && (
          <Badge className="bg-green-100 text-green-800 border-green-300">Finalized</Badge>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Total" value={summary.total_groups} />
        <StatCard label="Auto-approved" value={summary.auto_approved} className="text-green-700" />
        <StatCard label="Pending" value={summary.pending_review} className="text-yellow-700" />
        <StatCard label="Rejected" value={summary.rejected} className="text-red-700" />
      </div>

      {/* Finalize button */}
      {canFinalize && (
        <Button
          onClick={handleFinalize}
          disabled={finalize.isPending}
          className="w-full bg-blue-600 hover:bg-blue-700"
        >
          {finalize.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Finalizing...</>
          ) : (
            'Finalize Reconciliation Decisions'
          )}
        </Button>
      )}

      {!allDecided && !recon.finalized && (
        <p className="text-xs text-amber-600 text-center">
          {summary.pending_review} merge group(s) still need a decision before you can finalize.
        </p>
      )}

      {/* Filter */}
      <div className="flex items-center gap-1.5">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        {(['all', 'pending', 'approved', 'rejected'] as FilterMode[]).map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs capitalize"
            onClick={() => setFilter(f)}
          >
            {f}
          </Button>
        ))}
      </div>

      {/* Merge group cards */}
      <div className="space-y-3">
        {filteredGroups.map((group) => (
          <MergeGroupCard
            key={group.id}
            group={group}
            onApprove={handleApprove}
            onReject={handleReject}
            finalized={recon.finalized}
          />
        ))}
        {filteredGroups.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">
            No merge groups match the current filter.
          </p>
        )}
      </div>
    </div>
  )
}

function StatCard({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3 text-center">
      <p className={`text-2xl font-bold ${className ?? ''}`}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}
